import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReferralRewardRecord } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { copyToClipboardWithToast, NoddiBasicCard, NoddiCopyButton, NoddiInput } from 'noddi-ui-web';
import { ReferralLevelStepper, tracking } from 'noddi-web';
import { formatReferralProgramDiscountLabel } from '../../utils/referralProgramDisplay';

type Props = {
  referral: ReferralRewardRecord;
};

const ReferralLinkCopyButton = ({ referralLink }: { referralLink: string }) => {
  const copiedToClipboard = t`Copied to clipboard`;

  const copyReferralLink = () => {
    // oxlint-disable-next-line typescript/no-floating-promises -- toast + tracking on copy
    copyToClipboardWithToast(referralLink, copiedToClipboard);
    tracking.track('referralLinkCopiedDiscountsPage');
  };

  return (
    <NoddiCopyButton
      textToCopy={referralLink}
      variant='ghost'
      size='small'
      successMessage={copiedToClipboard}
      aria-label={t`Copy referral link`}
      onClick={copyReferralLink}
    />
  );
};

export const InviteFriendsCard = ({ referral }: Props) => {
  const referralLink = `${window.location.host}/referrals/${referral.referralCode}`;
  const currentRewardLabel = formatReferralProgramDiscountLabel(referral.currentReward ?? []);
  const inviteDiscountLabel = formatReferralProgramDiscountLabel(referral.inviteDiscounts ?? []);
  const howItWorksSteps = [
    <Trans key='step-1'>Share your link with a friend who is not already a customer</Trans>,
    inviteDiscountLabel ? (
      <Trans key='step-2'>
        Your friend opens the link and gets a {inviteDiscountLabel} discount coupon for their first booking.
      </Trans>
    ) : (
      <Trans key='step-2'>Your friend opens the link and gets a discount coupon to use on their first booking</Trans>
    ),
    <Trans key='step-3'>When your friend pays, you get your reward</Trans>,
    <Trans key='step-4'>
      You can invite as many as you want! The more friends you refer, the more reward you can get
    </Trans>
  ];
  const nextLevel = referral.nextLevel;
  const nextRewardLabel = nextLevel ? formatReferralProgramDiscountLabel(nextLevel.discounts) : null;
  const successfulCount = referral.successfulCount ?? 0;
  const nextLevelThreshold = nextLevel?.minNumResponses ?? 0;
  const filledSteps = Math.min(successfulCount, nextLevelThreshold);
  const referralsRemaining = Math.max(0, nextLevelThreshold - successfulCount);
  const showNextLevel =
    nextLevel != null && nextRewardLabel != null && nextRewardLabel !== '' && nextLevelThreshold > 0;

  return (
    <NoddiBasicCard className='flex flex-col divide-y divide-border p-4 md:p-5'>
      <div className='flex gap-3 pb-3'>
        <div className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
          <NoddiIcon name='Gift' size='medium' />
        </div>
        <div className='flex min-w-0 flex-col gap-0.5'>
          <p className='text-base font-semibold text-primary'>
            {currentRewardLabel ? (
              <Trans>Invite friends, earn {currentRewardLabel}</Trans>
            ) : (
              <Trans>Invite friends and earn rewards</Trans>
            )}
          </p>
          <p className='text-sm text-muted-foreground'>
            <Trans>Send the link to someone who is not already a customer</Trans>
          </p>
        </div>
      </div>

      <div className='py-3'>
        <NoddiInput
          value={referralLink}
          readOnly
          aria-label={t`Referral link`}
          className='h-8 border-border bg-muted font-mono shadow-none [&_input]:text-sm'
          endAdornment={<ReferralLinkCopyButton referralLink={referralLink} />}
          onFocus={(event) => event.target.select()}
          onClick={(event) => event.currentTarget.select()}
        />
      </div>

      <section className='flex flex-col gap-3 py-3'>
        <h3 className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
          <Trans>How it works</Trans>
        </h3>
        <ol className='flex list-none flex-col gap-3'>
          {howItWorksSteps.map((step, index) => (
            <li key={index} className='flex gap-3 text-sm'>
              <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary'>
                {index + 1}
              </span>
              <span className='text-balance'>{step}</span>
            </li>
          ))}
        </ol>
      </section>

      {showNextLevel && (
        <section className='flex flex-col gap-2 pt-3'>
          <h3 className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
            <Trans>Your reward</Trans>
          </h3>
          <div className='flex flex-col gap-1 text-sm'>
            {currentRewardLabel ? (
              <p className='font-semibold text-primary'>
                <Trans>{currentRewardLabel} per referral</Trans>
              </p>
            ) : null}
            <p className='text-muted-foreground'>
              {plural(referralsRemaining, {
                one: '# referral left until the next level, where you earn',
                other: '# referrals left until the next level, where you earn'
              })}{' '}
              {nextRewardLabel} <Trans>per referral</Trans>
            </p>
          </div>
          <ReferralLevelStepper completed={filledSteps} total={nextLevelThreshold} />
        </section>
      )}
    </NoddiBasicCard>
  );
};
